import React, { useState } from "react";
import { Alert, Box, Button, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import Labels from "../labels/Labels";

export default function AuditHistoryNoteForm({ memberId, companyId }) {
  const [notes, setNotes] = useState("");
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (text) =>
      HttpService.getAxiosClient().post(
        window.API_URL + "/member/kyc-history/note",
        { memberId, companyId, notes: text }
      ),
    {
      onSuccess: () => {
        setNotes("");
        queryClient.invalidateQueries(["secureInfo"]);
      },
    }
  );
  const responseError = mutation.error?.response?.data;
  const errorMessage =
    (typeof responseError === "string" && responseError) ||
    responseError?.description ||
    mutation.error?.message ||
    Labels.getLabel("memberSecureInfo.auditNoteError", "Could not save audit note");

  return (
    <Box
      role="form"
      aria-label={Labels.getLabel(
        "memberSecureInfo.addAuditNoteForm",
        "Add audit note"
      )}
      sx={{ mb: 3 }}
    >
      <TextField
        label={Labels.getLabel("memberSecureInfo.auditNote", "Audit note")}
        value={notes}
        onChange={(event) => {
          setNotes(event.target.value);
          mutation.reset();
        }}
        multiline
        minRows={3}
        fullWidth
        disabled={mutation.isLoading}
        inputProps={{ maxLength: 4000 }}
        helperText={Labels.getLabel(
          "memberSecureInfo.auditNoteHelp",
          "Saved with your username and time. Notes cannot be edited and do not change KYC status."
        )}
      />
      {mutation.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {errorMessage}
        </Alert>
      )}
      {mutation.isSuccess && (
        <Alert severity="success" sx={{ mt: 1 }}>
          {Labels.getLabel("memberSecureInfo.auditNoteSaved", "Audit note saved")}
        </Alert>
      )}
      <Button
        type="button"
        onClick={() => {
          if (
            notes.trim() &&
            notes.length <= 4000 &&
            !mutation.isLoading &&
            memberId &&
            companyId
          ) {
            mutation.mutate(notes.trim());
          }
        }}
        variant="contained"
        sx={{ mt: 1 }}
        disabled={
          !notes.trim() ||
          notes.length > 4000 ||
          mutation.isLoading ||
          !memberId ||
          !companyId
        }
      >
        {mutation.isLoading
          ? Labels.getLabel("memberSecureInfo.savingAuditNote", "Saving...")
          : Labels.getLabel("memberSecureInfo.addAuditNote", "Add note")}
      </Button>
    </Box>
  );
}
